import { defineStore } from "pinia";
import { propertydb, Tractsdb } from "../db/dexieConfig"; // Import the databases here
import * as turf from "@turf/turf";
import wellknown from "wellknown";
import axios from "axios";
import {
  collection,
  doc,
  addDoc,
  query,
  getDocs,
  setDoc,
  getDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore"; // Import Firestore functions
import { useNeightbourhoodStore } from "../stores/neigbourhoodStore.js";
import { usePersonaStore } from "../stores/PersonaStore.js";

// Add this utility function at the top level of the store
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const usePropStore = defineStore("prop", {
  state: () => ({
    idealHome: {},
    randomHomes: [],
    prioritizedHomes: [],
    propertyData: {},
    propertySpecs: {
      stepOne: {
        location: [],
        SearchType: "",
      },
      stepTwo: {
        importantlocations: [],
        AverageDay: "",
      },
      stepThree: {
        AnyThingElse: "",
      },
    },
    disableSearch: true,
    propDocId: "",
    allProperties: [],
    loading: false,
    lastFetchDate: null,
    recommendationCache: new Map(),
    recommendationLoadingStates: new Map(), // Track loading state per property
    error: null,
    // Add pagination and filtering state
    currentPage: 1,
    totalPages: 1,
    totalProperties: 0,
    itemsPerPage: 9,
    currentFilters: {},
    currentSortBy: "price_asc",
  }),
  actions: {
    resetStore() {
      this.$reset(); // This resets the state to the initial state defined above
    },
    async getPropertyData(personaid) {
      const db = useFirestore();
      try {
        const propRef = collection(
          db,
          "personadata",
          personaid,
          "PropertyQuery"
        );
        const querySnapshot = await getDocs(propRef);
        if (querySnapshot.empty) {
          console.log("No Data");
          return this.propertySpecs;
        } else {
          console.log("Data Data");
          querySnapshot.forEach((doc) => {
            this.propDocId = doc.id;
            this.idealHome = doc.data().idealHome;
            this.propertySpecs = doc.data().propertySpecs;
          });
          console.log(`Doc ID: ${this.propDocId},`);
          this.disableSearch = false;
          return true;
        }
      } catch {
        console.error("Error Occoured");
        return false;
      }
    },
    async CUPropertyData(personaid, data, propdocid) {
      const db = useFirestore();
      // Initialize a conversation thread
      try {
        if (!propdocid) {
          console.log("running true", propdocid);
          console.log("Run Create PropData initialize");
          const mydoc = await addDoc(
            collection(db, "personadata", personaid, "PropertyQuery"),
            {
              propertySpecs: data.propertySpecs,
              idealHome: data.idealHome,
            }
          );
          this.propDocId = mydoc.id;
          console.log(`Doc ID: ${this.propDocId}`);
          return "Completed New Doc";
        } else {
          console.log("running false", propdocid);
          const propdocRef = doc(
            db,
            "personadata",
            personaid,
            "PropertyQuery",
            this.propDocId
          );
          console.log(`Doc ID: ${this.propDocId}`, data);
          await updateDoc(propdocRef, {
            propertySpecs: data.propertySpecs,
            idealHome: data.idealHome,
          });
          console.log(`Doc ID updated: ${this.propDocId}`);
          return "Updated";
        }
      } catch (error) {
        console.error("Error initializing thread:", error);
      }
    },
    async fetchAndSaveRelevantProperties(
      personaId,
      state,
      isochrone,
      page = 1,
      filters = {},
      sortBy = "price_asc"
    ) {
      const db = useFirestore();
      this.loading = true;
      this.error = null;
      try {
        // Clear existing data before fetching new data
        if (page === 1) {
          await propertydb.clearAllData();
        }

        // Fetch fresh data from API with pagination and filters
        const response = await axios.post("/api/properties/fetchProperties2", {
          state,
          isochrone: JSON.stringify(isochrone),
          page,
          limit: this.itemsPerPage,
          filters,
          sortBy,
          docid: personaId,
        });

        // Update store state with new data
        if (page === 1) {
          this.allProperties = response.data.properties || [];
        } else {
          this.allProperties = [
            ...this.allProperties,
            ...response.data.properties,
          ];
        }

        // Update pagination state
        this.currentPage = response.data.metadata.page;
        this.totalPages = response.data.metadata.totalPages;
        this.totalProperties = response.data.metadata.total;
        this.currentFilters = filters;
        this.currentSortBy = sortBy;

        // Save to Dexie
        const propertiesToSave = response.data.properties.map((property) => ({
          ...property,
          location: [property.latitude, property.longitude].join(","),
        }));

        if (propertiesToSave.length > 0) {
          console.log(
            `💾 Saving ${propertiesToSave.length} properties to Dexie...`
          );
          await propertydb.propdb.bulkPut(propertiesToSave);
          await propertydb.updateMetadata(
            personaId,
            response.data.metadata.total
          );
        }

        return response.data;
      } catch (error) {
        this.error = error.message;
        console.error("Error fetching properties:", error);
        throw error;
      } finally {
        this.loading = false;
      }
    },
    async processProperties(personaid) {
      try {
        if (!this.allProperties || this.allProperties.length === 0) {
          this.prioritizedHomes = [];
          return;
        }

        // Retrieve the top tracts based on personaid and score
        let relevantTracts = await Tractsdb.tracts
          .where("personaid")
          .equals(personaid)
          .toArray();

        // If no tracts found, just return all properties
        if (!relevantTracts || relevantTracts.length === 0) {
          this.prioritizedHomes = this.allProperties;
          this.randomHomes = this.allProperties.slice(0, 6);
          return this.allProperties;
        }

        const topTracts = relevantTracts
          .sort((a, b) => b.total_score - a.total_score)
          .slice(0, 6);

        // Initialize an array to store properties per tract with overlap and geoid details
        const tractProperties = topTracts
          .map((tract) => {
            if (!tract.tract_geom) return { tractId: tract.id, properties: [] };

            const tractPolygon = wellknown.parse(tract.tract_geom);
            if (!tractPolygon) return { tractId: tract.id, properties: [] };

            // Filter properties within the tract polygon
            const propertiesInTract = this.allProperties
              .filter((property) => {
                if (!property.latitude || !property.longitude) return false;

                try {
                  const propertyPoint = turf.point([
                    property.longitude,
                    property.latitude,
                  ]);
                  return turf.booleanPointInPolygon(
                    propertyPoint,
                    tractPolygon
                  );
                } catch (error) {
                  return false;
                }
              })
              .map((property) => ({
                ...property,
                overlapPercentage: 100,
                geoid: tract.geoid,
                tractScore: tract.total_score,
              }));

            return { tractId: tract.id, properties: propertiesInTract };
          })
          .filter((tract) => tract.properties.length > 0);

        // Prioritize properties by tract score
        const prioritizedProperties = tractProperties
          .flatMap((tract) => tract.properties)
          .sort((a, b) => b.tractScore - a.tractScore);

        // If no properties found in tracts, use all properties
        if (prioritizedProperties.length === 0) {
          this.prioritizedHomes = this.allProperties;
          this.randomHomes = this.allProperties.slice(0, 6);
          return this.allProperties;
        }

        // Get random 6 properties from prioritized list
        const randomProperties = prioritizedProperties
          .sort(() => 0.5 - Math.random())
          .slice(0, 6);

        this.prioritizedHomes = prioritizedProperties;
        this.randomHomes = randomProperties;
        return tractProperties;
      } catch (error) {
        console.error("Error processing properties:", error);
        throw error;
      }
    },
    async getPropertyById(propertyId, _listingType, _state) {
      try {
        const authStore = useAuthStore();
        const personaId = authStore.getuid;
        const personaStore = usePersonaStore();
        const persona = await personaStore.getPersona(personaId);

        const neightbourhoodStore = useNeightbourhoodStore();

        if (personaStore.form?.movingTo?.detailed_address) {
          await neightbourhoodStore.setcitystatecounty(
            personaStore.form.movingTo.detailed_address
          );
        }

        const state = _state || neightbourhoodStore.us_state;

        let listingType;
        switch (_listingType) {
          case "buy":
            listingType = "ForSale";
            break;
          case "sale":
            listingType = "ForSale";
            break;
          case "rent":
            listingType = "ForRent";
            break;
          case "ForSale":
            listingType = "ForSale";
            break;
          case "ForRent":
            listingType = "ForRent";
            break;
          default:
            throw new Error(`Invalid listing type: ${_listingType ?? "undefined"}`);
        }

        const response = await axios.post("/api/properties/view", {
          id: propertyId,
          state: state,
          listingType: listingType,
        });

        if (response.data.success) {
          return response.data.property;
        } else {
          throw new Error("Failed to fetch property");
        }
      } catch (error) {
        console.error("Error fetching property by ID:", error);
        throw error;
      }
    },
    async getPropertyCaptions(propertyId, photo_urls) {
      try {
        const authStore = useAuthStore();
        const personaId = authStore.getuid;
        const personaStore = usePersonaStore();
        const persona = await personaStore.getPersona(personaId);

        const neightbourhoodStore = useNeightbourhoodStore();

        if (personaStore.form?.movingTo?.detailed_address) {
          await neightbourhoodStore.setcitystatecounty(
            personaStore.form.movingTo.detailed_address
          );
        }

        const state = neightbourhoodStore.us_state;

        const response = await axios.post("/api/caption", {
          property_id: propertyId,
          photo_urls: photo_urls,
          state: state,
        });

        if (response.data) {
          return response.data;
        } else {
          throw new Error("Failed to fetch captions");
        }
      } catch (error) {
        console.error("Error fetching captions:", error);
        throw error;
      }
    },
    async getFilteredProperties(filters) {
      this.loading = true;
      try {
        // Reset pagination when filters change
        this.currentPage = 1;
        this.currentFilters = filters;

        const neightbourhoodStore = useNeightbourhoodStore();
        const personaStore = usePersonaStore();

        // Fetch filtered properties from server
        const response = await this.fetchAndSaveRelevantProperties(
          personaStore.personadocid,
          neightbourhoodStore.us_state,
          personaStore.isoChrone,
          1,
          filters,
          filters.sortBy
        );

        return response.properties;
      } catch (error) {
        console.error("Error filtering properties:", error);
        return [];
      } finally {
        this.loading = false;
      }
    },
    async generatePropertyRecommendation(property) {
      console.log(
        "🤖 Starting generatePropertyRecommendation for property:",
        property.property_id
      );
      console.log("Property details:", {
        address: property.address,
        price: property.list_price,
        type: property.property_type,
        size: property.home_size,
      });

      // Set loading state for this property
      this.recommendationLoadingStates.set(property.property_id, true);
      console.log(
        "Set loading state to true for property:",
        property.property_id
      );

      try {
        // Check cache first
        const cachedRecommendation = this.recommendationCache.get(
          property.property_id
        );
        console.log("Checking recommendation cache...");
        if (cachedRecommendation) {
          console.log(
            "📦 Using cached recommendation for property:",
            property.property_id,
            cachedRecommendation
          );
          this.recommendationLoadingStates.set(property.property_id, false);
          return cachedRecommendation;
        }
        console.log(
          "No cached recommendation found, proceeding with generation"
        );

        const personaStore = usePersonaStore();
        const neightbourhoodStore = useNeightbourhoodStore();
        console.log("Stores initialized successfully");

        // Extract data from persona
        const userRequirements = personaStore.form?.property || {};
        const userPersona = personaStore.assumptions || {};
        const movingInformation = {
          location: personaStore.form?.location || [],
          moveDate: personaStore.form?.dateOfMove,
          movingFrom: personaStore.form?.movingFrom,
          reasonForMove: personaStore.form?.reasonForMove,
        };

        console.log("Extracted user data:", {
          requirements: userRequirements,
          persona: userPersona,
          moving: movingInformation,
        });

        try {
          console.log("Making API request to generate recommendation...");
          // Make async request using axios
          const response = await axios.post("/api/generateRecommendation", {
            property,
            userRequirements,
            userPersona,
            movingInformation,
          });
          console.log("Received API response:", response.data);

          if (!response.data.success) {
            console.error("API request failed:", response.data);
            throw new Error("Failed to generate recommendation");
          }

          // Cache the recommendation
          console.log(
            "Caching recommendation for property:",
            property.property_id
          );
          this.recommendationCache.set(property.property_id, response.data);
          console.log("Cached recommendation:", response.data);
          return response.data;
        } catch (error) {
          console.error(
            "❌ Error in generatePropertyRecommendation API call:",
            error
          );
          console.error("Error details:", {
            message: error.message,
            code: error.code,
            response: error.response?.data,
          });
          throw error;
        } finally {
          // Clear loading state
          console.log(
            "Clearing loading state for property:",
            property.property_id
          );
          this.recommendationLoadingStates.set(property.property_id, false);
        }

        // Return immediately to allow UI to show loading state
        return null;
      } catch (error) {
        console.error("❌ Error in generatePropertyRecommendation:", error);
        console.error("Full error details:", {
          message: error.message,
          stack: error.stack,
          code: error.code,
        });
        this.recommendationLoadingStates.set(property.property_id, false);
        throw error;
      }
    },
    async generateAndStoreRecommendations(properties, personaId) {
      console.log("🔄 Starting generateAndStoreRecommendations");
      console.log("Properties count:", properties.length);
      const db = useFirestore();
      try {
        const personaStore = usePersonaStore();
        const neightbourhoodStore = useNeightbourhoodStore();

        // Get user data for recommendations
        const userRequirements = this.propertySpecs;
        const userPersona = personaStore.form;
        const movingInformation = {
          location: neightbourhoodStore.us_city,
          state: neightbourhoodStore.us_state,
          moveDate: personaStore.form.dateOfMove,
        };
        // Get existing recommendations
        const recommendationsRef = collection(
          db,
          "personadata",
          personaId,
          "propertyRecommendations"
        );
        const existingRecsSnapshot = await getDocs(recommendationsRef);
        const existingRecs = new Map();
        existingRecsSnapshot.forEach((doc) => {
          existingRecs.set(doc.id, doc.data());
        });

        console.log("📚 Existing recommendations count:", existingRecs.size);

        // Filter properties that need recommendations
        const newProperties = properties.filter(
          (prop) =>
            !existingRecs.has(prop.property_id) &&
            !this.recommendationCache.has(prop.property_id)
        );
        console.log(
          "🆕 New properties needing recommendations:",
          newProperties.length
        );

        if (newProperties.length === 0) {
          console.log("✨ No new properties need recommendations");
          return;
        }

        // Generate recommendations in smaller batches with delay
        const batchSize = 9; // Reduced from 5 to 3 for better rate limiting
        const batches = Math.ceil(newProperties.length / batchSize);
        console.log(
          `🔄 Processing ${batches} batches of ${batchSize} properties each`
        );

        for (let i = 0; i < batches; i++) {
          console.log(`\n📦 Processing batch ${i + 1}/${batches}`);
          const batch = writeBatch(db);
          const start = i * batchSize;
          const end = Math.min(start + batchSize, newProperties.length);
          const batchProperties = newProperties.slice(start, end);

          // Generate recommendations sequentially with delay
          const recommendations = [];
          for (const property of batchProperties) {
            try {
              console.log(
                `\n🏠 Generating recommendation for property ${property.property_id}`
              );
              // Add delay between API calls
              if (recommendations.length > 0) {
                console.log("⏳ Waiting before next API call...");
                await delay(2000); // 2 second delay between calls
              }

              const recommendation = await this.generatePropertyRecommendation(
                property
              );
              if (recommendation) {
                recommendations.push({
                  propertyId: property.property_id,
                  ...recommendation,
                  generatedAt: new Date().toISOString(),
                });
              }
            } catch (error) {
              console.error(
                `❌ Error generating recommendation for property ${property.property_id}:`,
                error
              );
              // Add exponential backoff retry logic
              if (error.message?.includes("429")) {
                const retryDelay = Math.min(1000 * Math.pow(2, i), 10000); // Max 10 second delay
                console.log(
                  `⏳ Rate limited, waiting ${retryDelay}ms before retry...`
                );
                await delay(retryDelay);
                try {
                  const retryRecommendation =
                    await this.generatePropertyRecommendation(property);
                  if (retryRecommendation) {
                    recommendations.push({
                      propertyId: property.property_id,
                      ...retryRecommendation,
                      generatedAt: new Date().toISOString(),
                    });
                  }
                } catch (retryError) {
                  console.error("❌ Retry also failed:", retryError);
                }
              }
            }
          }

          // Store recommendations in Firebase
          const validRecs = recommendations.filter(Boolean);
          console.log(
            `✅ Successfully generated ${validRecs.length}/${batchProperties.length} recommendations`
          );

          validRecs.forEach((rec) => {
            const recRef = doc(recommendationsRef, rec.propertyId);
            batch.set(recRef, rec);
          });

          await batch.commit();
          console.log(`💾 Committed batch ${i + 1}/${batches} to Firebase`);

          // Add delay between batches
          if (i < batches - 1) {
            console.log("⏳ Waiting before next batch...");
            await delay(5000); // 5 second delay between batches
          }
        }

        console.log("✨ All recommendations generated and stored successfully");
      } catch (error) {
        console.error("❌ Error in generateAndStoreRecommendations:", error);
        throw error;
      }
    },
    async fetchPropertyRecommendation(propertyId, listingType, personaId) {
      const db = useFirestore();

      try {
        // First check the cache
        if (this.recommendationCache.has(propertyId)) {
          return this.recommendationCache.get(propertyId);
        }

        // If not in cache, check Firebase
        const recRef = doc(
          db,
          "personadata",
          personaId,
          "propertyRecommendations",
          propertyId
        );
        const recDoc = await getDoc(recRef);

        if (recDoc.exists()) {
          const data = recDoc.data();

          // Cache the result
          this.recommendationCache.set(propertyId, data);
          return data;
        }

        console.log(
          "🔄 No existing recommendation found, generating new one..."
        );
        // If no recommendation exists, generate one
        const property = await this.getPropertyById(propertyId, listingType);
        if (!property) {
          console.error("❌ Property not found:", propertyId);
          return null;
        }

        // Generate new recommendation
        const recommendation = await this.generatePropertyRecommendation(
          property
        );
        if (recommendation?.success) {
          const recommendationData = {
            ...recommendation.data,
            generatedAt: new Date().toISOString(),
          };

          // Save to Firebase
          try {
            await setDoc(recRef, recommendationData);
            console.log("✅ Saved new recommendation to Firebase");
            // Cache the result
            this.recommendationCache.set(propertyId, recommendationData);
            return recommendationData;
          } catch (error) {
            console.error("❌ Error saving recommendation to Firebase:", error);
            return recommendationData; // Still return the data even if saving fails
          }
        }

        console.log("❌ Failed to generate recommendation");
        return null;
      } catch (error) {
        console.error("❌ Error fetching property recommendation:", error);
        return null;
      }
    },
    async fetchBatchRecommendations(propertyIds, personaId, listingType) {
      console.log(
        "🔄 Fetching batch recommendations for properties:",
        propertyIds
      );
      const db = useFirestore();
      const recommendations = new Map();
      const missingRecommendations = [];

      // First check cache for all properties
      propertyIds.forEach((id) => {
        if (this.recommendationCache.has(id)) {
          recommendations.set(id, this.recommendationCache.get(id));
        } else {
          missingRecommendations.push(id);
        }
      });

      if (missingRecommendations.length === 0) {
        console.log("✅ All recommendations found in cache");
        return recommendations;
      }

      try {
        // Fetch missing recommendations from Firebase in batch
        const recommendationsRef = collection(
          db,
          "personadata",
          personaId,
          "propertyRecommendations"
        );
        const snapshot = await getDocs(
          query(
            recommendationsRef,
            where("__name__", "in", missingRecommendations)
          )
        );

        // Process Firebase results
        snapshot.forEach((doc) => {
          const data = doc.data();
          recommendations.set(doc.id, data);
          this.recommendationCache.set(doc.id, data);
          const index = missingRecommendations.indexOf(doc.id);
          if (index > -1) {
            missingRecommendations.splice(index, 1);
          }
        });

        // Generate missing recommendations in parallel
        if (missingRecommendations.length > 0) {
          console.log(
            "🔄 Generating missing recommendations:",
            missingRecommendations.length
          );

          const properties = await Promise.all(
            missingRecommendations.map((id) => this.getPropertyById(id, listingType))
          );

          const newRecommendations = await Promise.all(
            properties.filter(Boolean).map(async (property) => {
              try {
                const recommendation =
                  await this.generatePropertyRecommendation(property);
                if (recommendation?.success) {
                  const recommendationData = {
                    ...recommendation.data,
                    generatedAt: new Date().toISOString(),
                  };

                  // Save to Firebase
                  const recRef = doc(recommendationsRef, property.property_id);
                  await setDoc(recRef, recommendationData);

                  // Cache and return
                  this.recommendationCache.set(
                    property.property_id,
                    recommendationData
                  );
                  recommendations.set(property.property_id, recommendationData);
                }
              } catch (error) {
                console.error(
                  `❌ Error generating recommendation for property ${property.property_id}:`,
                  error
                );
              }
            })
          );
        }

        return recommendations;
      } catch (error) {
        console.error("❌ Error in fetchBatchRecommendations:", error);
        return recommendations;
      }
    },
    async loadNextPage() {
      if (this.currentPage < this.totalPages && !this.loading) {
        const nextPage = this.currentPage + 1;
        const neightbourhoodStore = useNeightbourhoodStore();
        const personaStore = usePersonaStore();

        await this.fetchAndSaveRelevantProperties(
          personaStore.personadocid,
          neightbourhoodStore.us_state,
          personaStore.isoChrone,
          nextPage,
          this.currentFilters,
          this.currentSortBy
        );
      }
    },
    //TODO: This needs to be more precise
    async fetchPropertyNeighbourhood(zipcode) {
      const neightbourhoodStore = useNeightbourhoodStore();
      const data = await neightbourhoodStore.fetchNeighborhoodByZipcode(
        zipcode
      );

      if (data.neighborhoods.length > 0) {
        return data.neighborhoods[0];
      } else {
        return null;
      }
    },

    async fetchPropertyTypes(state, listingType) {
      const response = await axios.get(`/api/propertyTypes?state=${state}&listingType=${listingType}`);
      return response.data.propertyTypes;
    },
  },
});
